/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useI18n } from "hooks/i18n/i18n";
import { SubContentsLayout } from "ui/SubContentsLayout";
import type { MyProductData } from "@spwn/types";
import type { ActiveTransaction } from "@spwn/types/firebase/firestore";

import Box from "@material-ui/core/Box";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { Store } from "store";
import { isOrderTransaction } from "features/orderModel";
import { ticketActions } from "modules/ticket";
import { scrollTop } from "utility";
import { useCreateUnprocessedOrderConfirmModal } from "features/useCreateUnprocessedOrderConfirmModal";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import {
  AccountReceipt,
  ReceiptBreakdownItem,
  ReceiptData,
} from "./receipt/AccountReceipt";
import { AccountSettlementHistoryItem } from "./AccountSettlementHistoryItem";
import { useFetchSettlementHistory } from "./useFetchSettlementHistory";

import {
  fulfillmentFirestoreId,
  useShippingStatus,
} from "features/purchases/useShippingStatus";
import { LegacyPortalShippingStatusDispatchStatus } from "@spwn-portal/grpc";

export type Settlement = ActiveTransaction & {
  products: ActiveTransaction["products"] & MyProductData[];
};

export const AccountSettlementHistory: React.FC = () => {
  const INITIAL_RECIPE_DATA = {
    effectiveDate: null,
    spwnId: null,
    userName: null,
    orderId: null,
    shippingFee: 0,
    serviceFee: 0,
    settlementFee: 0,
    total: 0,
    breakdown: null,
  };

  const { t } = useI18n();
  const dispatch = useDispatch();

  const user = useSelector((state: Store) => state.auth.user);

  const {
    status: fetchSettlementHistoryStatus,
    filteredSettlementHistory,
    settlementYearList,
    filterSettlementHistory,
  } = useFetchSettlementHistory();
  const { loading, createConfirmCvsPaymentData, renderConfirmCvsPayment } =
    useCreateUnprocessedOrderConfirmModal();

  const [isReceipt, setIsReceipt] = useState<boolean>(false);
  const [receiptData, setReceiptData] =
    // @ts-expect-error TS2345
    useState<ReceiptData>(INITIAL_RECIPE_DATA);

  useEffect(() => {
    dispatch(ticketActions.fetchMyTickets.started({}));
  }, [user.uid, dispatch]);

  const setSelectedSettlementHistory = (orderId: number, data: Settlement) => {
    const effectiveDate = new Date(orderId);
    const spwnId = user.uid;
    const userName = user.displayName;

    const shippingFee = data.shippingFee || 0; // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    const serviceFee = data.serviceFee || 0; // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    const settlementFee = data.settlementFee || 0; // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing

    const breakdown: ReceiptBreakdownItem[] = isOrderTransaction(data)
      ? data.lineItems.map((p) => ({
          eventTitle: null,
          productType: "goods" as const,
          name: p.name,
          price: p.price,
          quantity: p.quantity,
        }))
      : data.products.map((p) => ({
          eventTitle: p.eventTitle,
          productType: p.productType,
          name: p.name,
          price: p.price_jpy,
          quantity: p.count,
        }));

    // HOTFIX: totalPriceが存在しないケースが有るので計算する
    // const total = data.totalPrice;
    const total =
      breakdown.reduce((acc, cur) => {
        return acc + cur.price * cur.quantity;
      }, 0) +
      shippingFee +
      serviceFee +
      settlementFee;

    setIsReceipt(true);
    setReceiptData({
      effectiveDate,
      spwnId,
      // @ts-expect-error TS2322
      userName,
      orderId,
      shippingFee,
      serviceFee,
      settlementFee,
      total,
      breakdown,
    });

    scrollTop();
  };

  const resetState = () => {
    setIsReceipt(false);
    setReceiptData({
      // @ts-expect-error TS2322
      effectiveDate: null,
      // @ts-expect-error TS2322
      spwnId: null,
      // @ts-expect-error TS2322
      userName: null,
      // @ts-expect-error TS2322
      orderId: null,
      shippingFee: 0,
      serviceFee: 0,
      settlementFee: 0,
      total: 0,
      // @ts-expect-error TS2322
      breakdown: null,
    });
    scrollTop();
  };

  const fulfillmentFirestoreIds = useMemo(() => {
    return filteredSettlementHistory
      .map((history) => history.fulfillmentRefs?.map((ref) => ref.id))
      .flat()
      .filter((id): id is string => id !== undefined);
  }, [filteredSettlementHistory]);

  const { shippingStatuses } = useShippingStatus(fulfillmentFirestoreIds);

  const getShippingStatus = (
    unbrandedFulfillmentFirestoreId: string | undefined
  ) => {
    if (!unbrandedFulfillmentFirestoreId) {
      return undefined;
    }
    const parsed = fulfillmentFirestoreId.safeParse(
      unbrandedFulfillmentFirestoreId
    );
    if (!parsed.success) {
      return {
        status: LegacyPortalShippingStatusDispatchStatus.UNSPECIFIED,
        trackingUrl: undefined,
      };
    }
    const brandedFulfillmentFirestoreId = parsed.data;
    const shippingStatus = shippingStatuses.get(brandedFulfillmentFirestoreId);

    return shippingStatus;
  };

  // ローディングのアーリーリターン
  if (loading) {
    return (
      <div id="contents-layout">
        <LoaderLayout />
      </div>
    );
  }

  /**
   * 領収書を表示する（アーリーリターン）
   */
  if (isReceipt) {
    return <AccountReceipt receiptData={receiptData} resetState={resetState} />;
  }

  return (
    <SubContentsLayout>
      {renderConfirmCvsPayment()}

      <Box>
        <BreadcrumbNavigation
          breadcrumbs={[
            { label: "ホーム", link: "/" },
            { label: t("common.routes.account"), link: "/account" },
            { label: t("common.routes.settlementHistory"), link: null },
          ]}
        />
      </Box>

      <Box width={"90%"} maxWidth={"1080px"} margin={"0 auto"}>
        <Box
          display={"grid"}
          gridTemplateColumns={"1fr"}
          gridGap={"16px"}
          alignItems={"center"}
          padding={"40px 0"}
        >
          <Box component={"h2"} fontSize={"20px"}>
            {t("common.routes.settlementHistory")}
          </Box>
        </Box>

        {/* 絞り込み機能を実装する */}
        <Box marginBottom={"24px"}>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"right"}
            gridGap={"8px"}
          >
            <Box fontSize={"14px"} fontWeight={"bold"}>
              絞り込み
            </Box>
            <Box
              component={"select"}
              width={"auto"}
              padding={"8px 36px 8px 8px"}
              fontSize={"14px"}
              style={{
                borderRadius: "4px",
              }}
              onChange={(event) => {
                const target = event.target as HTMLSelectElement;
                const value =
                  target.value === "none" ? null : Number(target.value);
                filterSettlementHistory(value);
              }}
            >
              <option value="none">すべて</option>
              {settlementYearList.map((yearInfo) => (
                <option key={yearInfo.year} value={yearInfo.year}>
                  {yearInfo.year}年（{yearInfo.numOfSettlements}件）
                </option>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          display={["none", "grid"]}
          gridTemplateColumns={"3fr 9fr 3fr 2fr 2fr 1fr"}
          gridGap={"24px"}
          alignItems={"center"}
          padding={["0px 4px 8px", "0px 16px 8px"]}
          fontSize={"14px"}
          fontWeight={"600"}
          borderBottom={"1px solid #000"}
        >
          <Box>注文日</Box>
          <Box>決済内容</Box>
          <Box textAlign={"center"}>支払い方法</Box>
          <Box textAlign={"center"}>決済状況</Box>
          <Box textAlign={"right"}>
            {t("settlementHistory.settlementAmount")}
          </Box>
          <Box textAlign={"center"}>領収書</Box>
        </Box>

        {fetchSettlementHistoryStatus === "LOADING" && (
          <EmptySettlementHistory text={"決済履歴を読み込んでいます"} />
        )}

        {fetchSettlementHistoryStatus === "EMPTY" && (
          <EmptySettlementHistory text={"決済履歴はありません。"} />
        )}
        {fetchSettlementHistoryStatus === "SUCCESS" &&
          filteredSettlementHistory.map((history, index) => {
            const shippingStatus = getShippingStatus(
              history.fulfillmentRefs?.[0]?.id
            );
            return (
              <AccountSettlementHistoryItem
                key={index}
                history={history}
                setSelectedSettlementHistory={setSelectedSettlementHistory}
                createConfirmCvsPaymentData={createConfirmCvsPaymentData}
                dispatchStatus={shippingStatus?.status}
                trackingUrl={shippingStatus?.trackingUrl}
              />
            );
          })}
      </Box>
    </SubContentsLayout>
  );
};

const EmptySettlementHistory: React.FC<{
  text: string;
}> = ({ text }) => {
  return (
    <Box
      padding={"80px 0"}
      fontSize={"14px"}
      fontWeight={"bold"}
      textAlign={"center"}
    >
      {text}
    </Box>
  );
};
